import { useEffect, useRef, useState } from "react";
import type { ConversationResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { duration, label } from "../data/display.ts";
import { mediaUrl, type Part } from "../data/media.ts";
import type { StoredMessage } from "../data/store.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Icon } from "../ui/Icon.tsx";
import { usePlayer } from "./player.ts";
import { barsOf } from "./waveform.ts";
import { videoAt } from "../audio/videoTrack.ts";
import { callCard } from "../calls/callCard.ts";
import { LocationCard } from "../location/LocationCard.tsx";
import type { CallResponse } from "../api/contract.gen.ts";
import { RichText } from "./RichText.tsx";
import { ImageEditor } from "../edit/ImageEditor.tsx";
import styles from "./Message.module.css";

/** One message as a bubble (docs/web-designs screen 2): voice with sections, text, media, place. */
export function Message({ message, conversation, onTopic, onOpenBoard, onOpenDocument }: { message: StoredMessage; conversation: ConversationResponse; onTopic: (topicId: string) => void; onOpenBoard?: (boardId: string) => void; onOpenDocument?: (id: string) => void }) {
  const { store } = useServices();
  const r = message.response;
  const mine = r.sender_id === store.me;
  const sender = conversation.members.find((m) => m.user.id === r.sender_id)?.user;
  const reactions = r.reactions ?? [];
  const showSender = !mine && conversation.kind === "group";
  return (
    <div className={mine ? `${styles.row} ${styles.mine}` : styles.row} data-message={r.id}>
      {!mine && conversation.kind === "group" && <Avatar id={r.sender_id} name={label(sender)} size={32} />}
      <div className={styles.bubble}>
        {showSender && <div className={styles.sender}>{label(sender)}</div>}
        {r.reply_to && <ReplyQuote conversation={conversation} reply={r.reply_to} />}
        {r.board_id && r.board_card ? (
          <button className={styles.boardCard} onClick={() => onOpenBoard?.(r.board_id!)}>
            <Icon name="board" size={22} />
            <span>
              <b>{(message.content.text ?? "").replace(/^🖼\s*Neues Canvas:\s*/, "") || "Canvas"}</b>
              <small>Canvas öffnen</small>
            </span>
          </button>
        ) : (
          <>
            {(r.board_id || r.document_id || r.app_id || r.game_id) && (
              <button className={styles.context} onClick={() => (r.board_id ? onOpenBoard?.(r.board_id) : r.document_id && onOpenDocument?.(r.document_id))} disabled={!r.board_id && !r.document_id}>
                {r.board_id ? "Canvas" : r.document_id ? "Dokument" : r.app_id ? "Mini-App" : "Spiel"}
              </button>
            )}
            <Body message={message} mine={mine} onTopic={onTopic} senderName={label(sender)} />
          </>
        )}
        <span className={styles.meta}>
          {r.edited_at && !r.deleted && "bearbeitet · "}
          {new Date(r.created_at).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}
          {mine && <Status status={r.status} />}
        </span>
        {reactions.length > 0 && (
          <span className={styles.reactions}>
            {[...new Set(reactions.map((x) => x.emoji))].join(" ")} {reactions.length > 1 ? reactions.length : ""}
          </span>
        )}
      </div>
    </div>
  );
}

const KIND_LABELS: Record<string, string> = { voice: "Sprachnachricht", image: "Foto", video: "Video", file: "Datei", gif: "GIF", location: "Standort" };

/** The message this one answers, as a line above it; a click scrolls to it. */
function ReplyQuote({ conversation, reply }: { conversation: ConversationResponse; reply: { message_id: string; segment_idx?: number | null } }) {
  const { store } = useServices();
  const original = store.message(conversation.id, reply.message_id);
  const who = original && (original.response.sender_id === store.me ? "Du" : label(conversation.members.find((m) => m.user.id === original.response.sender_id)?.user));
  const what = original ? (original.content.text?.split("\n")[0] ?? KIND_LABELS[original.response.kind ?? ""] ?? "Nachricht") : "Nachricht";
  return (
    <button
      className={styles.quote}
      onClick={() => document.querySelector(`[data-message="${reply.message_id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
    >
      {who && <b>{who}</b>}
      <span>{what}</span>
    </button>
  );
}

function Status({ status }: { status: string | null | undefined }) {
  const titles: Record<string, string> = { heard: "Angehört", read: "Gelesen", delivered: "Zugestellt", sent: "Gesendet" };
  const blue = status === "heard" || status === "read";
  return (
    <span className={blue ? `${styles.status} ${styles.seen}` : styles.status} title={titles[status ?? "sent"] ?? "Gesendet"}>
      <Icon name={status === "sent" || !status ? "check" : "ticks"} size={15} />
    </span>
  );
}

function Body({ message, mine, onTopic, senderName }: { message: StoredMessage; mine: boolean; onTopic: (topicId: string) => void; senderName: string }) {
  const r = message.response;
  const c = message.content;
  if (r.deleted) return <em className={styles.muted}>Nachricht gelöscht</em>;
  if (c.broken) return <em className={styles.muted}>Diese Nachricht lässt sich nicht öffnen.</em>;
  switch (r.kind) {
    case "voice":
      return <Voice message={message} mine={mine} onTopic={onTopic} senderName={senderName} />;
    case "image":
      return (
        <>
          <Picture message={message} />
          {c.text && <RichText text={c.text} />}
        </>
      );
    case "video":
      return (
        <>
          <Video message={message} />
          {c.text && <RichText text={c.text} />}
        </>
      );
    case "file":
      return <File message={message} />;
    case "gif":
      // url is the animated picture (still only stands in for it while it loads, in the app).
      return c.gif ? <img className={styles.gif} src={c.gif.url} alt={c.gif.title} width={Math.min(c.gif.w, 280)} style={{ aspectRatio: `${c.gif.w} / ${c.gif.h}` }} /> : null;
    case "location":
      return <LocationCard message={message} mine={mine} senderName={senderName} />;
    default:
      if (r.call) return <CallCardView call={r.call} />;
      // Game cards carry no text of their own; everything else on a page (document, mini app, canvas)
      // is a normal message with a label above it.
      if (r.game_id && !c.text) return <span className={styles.muted}>Spiel – im Browser bald, bis dahin in der App</span>;
      return (
        <>
          {c.text ? <RichText text={c.text} /> : null}
          {c.linkPreview && (
            <a className={styles.preview} href={c.linkPreview.url} target="_blank" rel="noopener noreferrer nofollow">
              {c.linkPreview.image && <img src={`data:image/jpeg;base64,${c.linkPreview.image}`} alt="" />}
              <b>{c.linkPreview.title}</b>
              {c.linkPreview.siteName && <span>{c.linkPreview.siteName}</span>}
            </a>
          )}
        </>
      );
  }
}

function useMedia(message: StoredMessage, part: Part, enabled = true): string | undefined {
  const services = useServices();
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    mediaUrl(message, part, () => services.refresh(message)).then((u) => alive && setUrl(u), () => undefined);
    return () => {
      alive = false;
    };
  }, [message.response.id, part, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return url;
}

function Voice({ message, mine, onTopic, senderName }: { message: StoredMessage; mine: boolean; onTopic: (topicId: string) => void; senderName: string }) {
  const { player, store } = useServices();
  const state = usePlayer(player);
  const services = useServices();
  const r = message.response;
  const current = state.track?.messageId === r.id;
  const position = current ? state.positionMs : 0;
  const bars = barsOf(message.content.waveform, 54);
  const played = r.duration_ms ? Math.round((position / r.duration_ms) * bars.length) : 0;
  const segments = r.segments ?? [];
  const sections = segments.map((s) => ({ startMs: s.start_ms, title: s.title }));

  async function play(fromMs = 0) {
    if (current && fromMs === 0) return player.toggle();
    const url = await mediaUrl(message, "audio", () => services.refresh(message));
    await player.play({ messageId: r.id, conversationId: r.conversation_id, senderName: mine ? "Du" : senderName, durationMs: r.duration_ms, sections, reportHeard: r.sender_id !== store.me }, url, fromMs);
  }

  const now = current ? segments.findLast((s) => s.start_ms <= position) : undefined;
  return (
    <>
      <div className={styles.voice}>
        <button className={styles.play} onClick={() => void play()} aria-label={current && state.playing ? "Pause" : "Abspielen"}>
          <Icon name={current && state.playing ? "pause" : "play"} />
        </button>
        <div
          className={styles.wave}
          onClick={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            void play(Math.round(((e.clientX - box.left) / box.width) * r.duration_ms));
          }}
        >
          {bars.map((h, i) => (
            <span key={i} className={i < played ? styles.played : undefined} style={{ height: `${Math.max(3, h * 32)}px` }} />
          ))}
        </div>
        <span className={styles.duration}>{current ? `${duration(position)} / ${duration(r.duration_ms)}` : duration(r.duration_ms)}</span>
      </div>
      {r.video_segments && r.video_segments.length > 0 && r.media && <VoiceVideo message={message} current={current} />}
      {r.processing === "pending" && <div className={styles.muted}>Wird transkribiert …</div>}
      {segments.length > 0 ? (
        <div className={styles.sections}>
          {segments.map((s) => (
            <div key={s.idx} className={now?.idx === s.idx ? `${styles.section} ${styles.now}` : styles.section}>
              <button className={styles.ts} onClick={() => void play(s.start_ms)}>
                {duration(s.start_ms)}
              </button>
              <span>{s.title}</span>
              {s.topic_id && (
                <button className={styles.tag} onClick={() => onTopic(s.topic_id!)}>
                  <TopicName id={s.topic_id} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        r.transcript?.text && <div className={styles.transcript}>{r.transcript.text}</div>
      )}
    </>
  );
}

function TopicName({ id }: { id: string }) {
  const { store } = useServices();
  return <>{store.topics.get(id)?.title ?? "Thema"}</>;
}

function Picture({ message }: { message: StoredMessage }) {
  const services = useServices();
  const [full, setFull] = useState(false);
  const [editing, setEditing] = useState<Blob>();
  const thumb = useMedia(message, "thumb", Boolean(message.response.media?.thumb_url));
  const original = useMedia(message, "media", full || !message.response.media?.thumb_url);
  const m = message.response.media;
  const ratio = m?.width && m?.height ? m.width / m.height : 4 / 3;
  return (
    <>
      <button className={styles.picture} style={{ aspectRatio: String(ratio) }} onClick={() => setFull(true)} aria-label="Bild groß">
        {(original ?? thumb) && <img src={original ?? thumb} alt="" />}
      </button>
      {full && original && !editing && (
        <div className={styles.lightbox} onClick={() => setFull(false)} role="dialog" aria-label="Bild">
          <img src={original} alt="" />
          <div className={styles.lightboxBar}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                void fetch(original)
                  .then((r) => r.blob())
                  .then(setEditing);
              }}
            >
              <Icon name="edit" size={18} /> Bearbeiten
            </button>
          </div>
        </div>
      )}
      {editing && (
        <ImageEditor
          source={editing}
          send={{ canReply: true }}
          onClose={() => setEditing(undefined)}
          onDone={async ({ file, caption, asReply }) => {
            await services.sendFile(message.response.conversation_id, file, caption, asReply ? message.response.id : undefined);
            setEditing(undefined);
            setFull(false);
          }}
        />
      )}
    </>
  );
}

function Video({ message }: { message: StoredMessage }) {
  const [load, setLoad] = useState(false);
  const thumb = useMedia(message, "thumb", Boolean(message.response.media?.thumb_url));
  const video = useMedia(message, "media", load);
  if (video) return <video className={styles.video} src={video} controls autoPlay />;
  return (
    <button className={styles.picture} onClick={() => setLoad(true)} aria-label="Video abspielen">
      {thumb && <img src={thumb} alt="" />}
      <span className={styles.overlay}>
        <Icon name="play" size={36} />
      </span>
    </button>
  );
}

function File({ message }: { message: StoredMessage }) {
  const services = useServices();
  const m = message.response.media;
  const name = message.content.fileName ?? "Datei";
  async function download() {
    const url = await mediaUrl(message, "media", () => services.refresh(message));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
  }
  return (
    <button className={styles.file} onClick={() => void download()}>
      <Icon name="doc" size={28} />
      <span>
        <b>{name}</b>
        {m && <small>{(m.bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB</small>}
      </span>
      <Icon name="download" />
    </button>
  );
}

/**
 * The silent video track of a voice message, following the voice player like the app's
 * VideoFollower: the picture of the current segment, seek when it is more than 800 ms off, speed
 * like the voice; where the message has no picture, the last frame stays dimmed.
 */
function VoiceVideo({ message, current }: { message: StoredMessage; current: boolean }) {
  const { player } = useServices();
  const state = usePlayer(player);
  const thumb = useMedia(message, "thumb", Boolean(message.response.media?.thumb_url));
  const video = useMedia(message, "media", current);
  const element = useRef<HTMLVideoElement>(null);
  const segments = message.response.video_segments ?? [];
  const target = current ? videoAt(segments, state.positionMs) : null;

  useEffect(() => {
    const v = element.current;
    if (!v || !video) return;
    v.playbackRate = state.rate;
    if (target === null || !state.playing) {
      v.pause();
      if (target !== null && Math.abs(v.currentTime * 1000 - target) > 800) v.currentTime = target / 1000;
      return;
    }
    if (Math.abs(v.currentTime * 1000 - target) > 800) v.currentTime = target / 1000;
    if (v.paused) void v.play().catch(() => undefined);
  }, [target, state.playing, state.rate, video]);

  const m = message.response.media!;
  const ratio = m.width && m.height ? m.width / m.height : 16 / 9;
  return (
    <div className={styles.voiceVideo} style={{ aspectRatio: String(ratio) }}>
      {current && video ? (
        <video ref={element} src={video} muted playsInline className={target === null ? styles.dimmed : undefined} />
      ) : (
        thumb && <img src={thumb} alt="" />
      )}
      {!current && <span className={styles.videoChip}>Mit Video</span>}
    </div>
  );
}

/** "Verpasster Sprachanruf", "Ausgehender Videoanruf · 3:12"; a click calls back the same way. */
function CallCardView({ call }: { call: CallResponse }) {
  const { calls, store } = useServices();
  const card = callCard(call, store.me);
  return (
    <button className={styles.callCard} onClick={() => void calls.start(call.conversation_id, call.kind === "video" ? "video" : "voice")} title="Zurückrufen">
      <span className={card.missed ? `${styles.callIcon} ${styles.missed}` : styles.callIcon}>
        <Icon name={call.kind === "video" ? "video" : "call"} size={18} />
      </span>
      <span>
        <b className={card.missed ? styles.missedText : undefined}>{card.text}</b>
        {card.detail && <small>{card.detail}</small>}
      </span>
    </button>
  );
}
